import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Pencil, PlayCircle } from "lucide-react";

const empty = { title: "", description: "", recording_url: "", status: "proximo" };

const AdminTalleres = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/talleres").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const resetForm = () => { setEditingId(null); setForm(empty); setFile(null); };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({ title: t.title || "", description: t.description || "", recording_url: t.recording_url || "", status: t.status || "proximo" });
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/talleres/${editingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Taller actualizado");
      } else {
        await api.post("/talleres", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Taller publicado");
      }
      resetForm();
      load();
    } catch { toast.error("No se pudo guardar el taller"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este taller?")) return;
    await api.delete(`/talleres/${id}`);
    toast.success("Taller eliminado");
    load();
  };

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
      <form onSubmit={submit} data-testid="taller-form" className="bg-white border border-[#E5E5E5] rounded-sm p-6 h-fit">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2">
            <Upload size={18} className="text-brand-magenta" /> {editingId ? "Editar taller" : "Nuevo taller"}
          </h2>
          {editingId && <button type="button" onClick={resetForm} data-testid="taller-cancel" className="text-sm text-brand-ink/60 hover:text-brand-ink">Cancelar</button>}
        </div>
        <p className="mt-1 text-sm text-brand-ink/60">Aparece en la página de Tejiendo Redes, debajo del Taller 1.</p>
        <div className="mt-5 space-y-4">
          <div>
            <label className="text-sm font-semibold text-brand-ink">Título</label>
            <input required data-testid="taller-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="📚 Taller 2 · …" className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Información (una línea por dato)</label>
            <textarea rows={5} data-testid="taller-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={"Imparte: …\nFecha: …\nModalidad: en línea\nHorario: …"} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Estado</label>
            <select data-testid="taller-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta bg-white">
              <option value="proximo">Próximo</option>
              <option value="finalizado">Finalizado</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Enlace de la grabación (opcional)</label>
            <input type="url" data-testid="taller-recording" value={form.recording_url} onChange={(e) => setForm({ ...form, recording_url: e.target.value })} placeholder="https://… (YouTube, Drive, Zoom)" className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
          </div>
          <div>
            <label className="text-sm font-semibold text-brand-ink">Imagen (PNG){editingId ? " — opcional (déjala vacía para conservar la actual)" : ""}</label>
            <input type="file" accept="image/*" data-testid="taller-file" onChange={(e) => setFile(e.target.files[0])} className="mt-1.5 w-full text-sm border border-[#E5E5E5] rounded-sm px-4 py-2.5 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-brand-ink file:text-white" />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="save-taller-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-magenta text-white font-semibold py-3 rounded-sm hover:bg-brand-orange transition-colors disabled:opacity-60">
          {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Publicar taller"}
        </button>
      </form>

      <div>
        <h2 className="font-heading text-lg font-bold text-brand-ink mb-4">Talleres publicados</h2>
        {items.length === 0 ? (
          <p className="text-brand-ink/50">Aún no hay talleres adicionales.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {items.map((t) => (
              <div key={t.id} data-testid={`admin-taller-${t.id}`} className="bg-white border border-[#E5E5E5] rounded-sm p-4 flex items-center gap-3">
                {t.image_ref ? (
                  <img src={`${API}/media/${t.image_ref}`} alt={t.title} className="w-16 h-12 rounded-sm object-cover flex-shrink-0" />
                ) : (
                  <span className="w-16 h-12 rounded-sm bg-[#F0F0F0] flex items-center justify-center flex-shrink-0 text-brand-ink/40 text-xs">Sin img</span>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-brand-ink truncate">{t.title}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${t.status === "finalizado" ? "bg-[#E6E6E6] text-brand-ink/60" : "bg-brand-lime text-brand-ink"}`}>{t.status === "finalizado" ? "Finalizado" : "Próximo"}</span>
                    {t.recording_url && <span className="inline-flex items-center gap-1 text-xs text-brand-magenta"><PlayCircle size={12} /> con grabación</span>}
                  </div>
                </div>
                <button data-testid={`edit-taller-${t.id}`} onClick={() => startEdit(t)} className="p-2 text-brand-ink hover:text-brand-magenta"><Pencil size={16} /></button>
                <button data-testid={`delete-taller-${t.id}`} onClick={() => remove(t.id)} className="p-2 text-brand-ink hover:text-red-600"><Trash2 size={17} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTalleres;
